<template>
  <div class="gtree" v-click-outside="close">
    <button type="button" class="gtree-btn" :class="{ open }" @click="toggle">
      <span>{{ btnText }}</span>
      <span class="caret">▼</span>
    </button>
    <div class="gtree-panel" :class="{ open }">
      <div class="gs-search">
        <input v-model="kw" :placeholder="$t('chi68')" @input="onSearch" />
      </div>
      <div class="gs-tree" v-loading="treeLoading">
        <!-- 组织结构树：设备挂在所属组织节点的下一级，勾选设备/勾选组织即选设备；默认只展开一级，二级及以下折叠 -->
        <el-tree
          v-if="treeData.length > 0"
          ref="tree"
          node-key="key"
          :data="treeData"
          :props="{ children: 'child', label: 'name' }"
          :default-expanded-keys="rootKeys"
          show-checkbox
          :expand-on-click-node="false"
          :filter-node-method="filterNode"
          @check="onCheck"
        >
          <span class="gt-node" :class="{ dev: data.isDevice }" slot-scope="{ data }" @click.stop="onNodeClick(data)">
            <span class="gt-name">{{ data.name }}</span>
            <span v-if="!data.isDevice && data.dev_count" class="gt-count">{{ data.dev_count }}</span>
            <span
              v-if="data.isDevice"
              class="gt-dot"
              :style="{ background: data.run_state > 0 ? '#36c78c' : '#ff6b6b' }"
            ></span>
          </span>
        </el-tree>
        <div v-if="treeData.length === 0 && !treeLoading" class="gs-empty">{{ $t('chi9') }}</div>
      </div>
      <div class="gs-foot">
        <span>{{ value.length }} / {{ devices.length }} {{ $t('chi70') }}</span>
        <div class="gs-foot-btns">
          <button type="button" class="gs-clear" @click="clearAll">{{ $t('chi160') }}</button>
          <button type="button" class="gs-done" @click="done">{{ $t('chi67') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "GroupDeviceTree",
  props: {
    value: { type: Array, default: () => [] }, // 选中设备 ID 列表，空 = 全部
  },
  data() {
    return {
      open: false,
      kw: "",
      groupTree: [],
      devices: [],
      groupReady: false,
      devReady: false,
    };
  },
  computed: {
    treeLoading() {
      return !(this.groupReady && this.devReady);
    },
    // 组织树 + 设备节点组装：设备按 group_id 挂到组织节点 child 末尾
    treeData() {
      if (!this.groupReady || !this.devReady) return [];
      const byGroup = {};
      let ungrouped = [];
      this.devices.forEach((d) => {
        const node = {
          key: `d_${d.id}`,
          id: d.id,
          name: d.name,
          sn: d.sn,
          run_state: d.run_state,
          isDevice: true,
          child: [],
        };
        if (d.group_id !== undefined && d.group_id !== null && d.group_id !== "") {
          (byGroup[d.group_id] = byGroup[d.group_id] || []).push(node);
        } else {
          ungrouped.push(node);
        }
      });
      const groups = JSON.parse(JSON.stringify(this.groupTree));
      const attach = (node) => {
        node.key = `g_${node.id}`;
        const devs = byGroup[node.id] || [];
        delete byGroup[node.id];
        const childCount = (node.child || []).reduce((s, c) => s + attach(c), 0);
        node.child = [...(node.child || []), ...devs];
        node.dev_count = devs.length + childCount;
        return node.dev_count;
      };
      groups.forEach((root) => attach(root));
      // group_id 在树中找不到归属的设备，统一挂到未分组
      ungrouped = ungrouped.concat(Object.values(byGroup).flat());
      if (ungrouped.length > 0) {
        groups.push({
          key: "g_ungrouped",
          id: "g_ungrouped",
          name: this.$t("chi158"),
          child: ungrouped,
          dev_count: ungrouped.length,
        });
      }
      return groups;
    },
    // 默认展开的一级节点（根组织），二级及以下保持折叠
    rootKeys() {
      return this.treeData.map((n) => n.key);
    },
    btnText() {
      const n = this.value.length;
      return n === this.devices.length && n > 0
        ? `${this.$t("chi157")} (${n})`
        : this.$t("chi64", { n });
    },
  },
  created() {
    this.getGroupTree();
    this.getDeviceList();
  },
  methods: {
    getGroupTree() {
      this.$http.post("/admin/access/groupList", {}, (data) => {
        this.groupTree = data || [];
        this.groupReady = true;
      });
    },
    getDeviceList() {
      // 传参与 ProgramSetting.vue 一致：只传 group_ids，响应直接为设备数组
      this.$http.post(
        "/admin/device/groupDeviceList",
        { group_ids: sessionStorage.getItem("group_ids") },
        (data) => {
          this.devices = (data || []).map((d) => ({
            id: d.id,
            name: d.name,
            sn: d.sn,
            run_state: d.run_state,
            group_id: d.group_id,
          }));
          this.devReady = true;
          // 通知父组件全量设备 ID（getDeviceBootLog 的 device_ids 必填，空选时兜底用）
          this.$emit("loaded", this.devices.map((d) => d.id));
        }
      );
    },
    onSearch() {
      this.$refs.tree && this.$refs.tree.filter(this.kw.trim());
    },
    filterNode(value, data) {
      if (!value) return true;
      if (data.isDevice) {
        return data.name.toLowerCase().includes(value) || data.sn.toLowerCase().includes(value);
      }
      return data.name.indexOf(value) !== -1;
    },
    // 只统计设备叶子节点的勾选（勾选组织节点 = 勾选其下全部设备）；传出设备 ID
    onCheck() {
      const nodes = this.$refs.tree.getCheckedNodes(true);
      this.$emit("input", nodes.filter((n) => n.isDevice).map((n) => n.id));
    },
    // 点击设备名称行 = 切换勾选（点击组织名称行不处理，勾选请用复选框）
    onNodeClick(data) {
      if (!data.isDevice) return;
      const tree = this.$refs.tree;
      if (!tree) return;
      const checked = tree.getCheckedNodes(true).some((n) => n.key === data.key);
      tree.setChecked(data.key, !checked);
      const nodes = tree.getCheckedNodes(true);
      this.$emit("input", nodes.filter((n) => n.isDevice).map((n) => n.id));
    },
    // 打开面板时，把 value 回显到树上
    syncChecked() {
      this.$nextTick(() => {
        this.$refs.tree &&
          this.$refs.tree.setCheckedKeys(
            this.devices.filter((d) => this.value.includes(d.id)).map((d) => `d_${d.id}`)
          );
      });
    },
    toggle() {
      this.open = !this.open;
      if (this.open) this.syncChecked();
    },
    done() {
      this.open = false;
      this.$emit("done");
    },
    // 清除所有勾选：树节点全部取消，选中设备清空
    clearAll() {
      this.$refs.tree && this.$refs.tree.setCheckedKeys([]);
      this.$emit("input", []);
    },
    close() {
      this.open = false;
    },
  },
  directives: {
    "click-outside": {
      bind(el, binding) {
        el._clickOutside = (e) => {
          if (!el.contains(e.target)) binding.value(e);
        };
        document.addEventListener("click", el._clickOutside);
      },
      unbind(el) {
        document.removeEventListener("click", el._clickOutside);
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.gtree {
  position: relative;
  min-width: 230px;
}
.gtree-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 38px;
  width: 100%;
  justify-content: space-between;
  background: #fff;
  border: 1px solid #d7e5ed;
  border-radius: 9px;
  padding: 8px 12px;
  font-size: 14px;
  color: #314356;
  cursor: pointer;
  .caret {
    font-size: 10px;
    opacity: 0.65;
    transition: 0.2s;
  }
  &.open .caret {
    transform: rotate(180deg);
  }
}
.gtree-panel {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  width: 340px;
  background: #fff;
  border: 1px solid #dcecf4;
  border-radius: 12px;
  box-shadow: 0 16px 44px rgba(20, 70, 90, 0.18);
  z-index: 60;
  display: none;
  overflow: hidden;
  &.open {
    display: block;
  }
}
.gs-search {
  padding: 10px 12px;
  border-bottom: 1px solid #dcecf4;
  input {
    box-sizing: border-box; /* 抵消自身 padding+border，避免 width:100% 溢出 */
    width: 100%;
    min-height: 32px;
    padding: 6px 10px;
    font-size: 13px;
    border-radius: 8px;
    border: 1px solid #d7e5ed;
    outline: none;
  }
}
.gs-tree {
  min-height: 120px;
  max-height: 300px;
  overflow: auto;
  padding: 6px;

  .gt-node {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    padding-right: 6px;

    /* 设备行可点击勾选 */
    &.dev {
      cursor: pointer;

      &:hover .gt-name {
        color: #119fdb;
      }
    }

    .gt-name {
      font-weight: 700;
      color: #314356;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    /* 设备节点：名称不加粗，与组织结构区分 */
    &.dev .gt-name {
      font-weight: 400;
      color: #4a6172;
    }
    .gt-count {
      font-size: 11px;
      color: #119fdb;
      background: #eaf7ff;
      border-radius: 999px;
      padding: 0 7px;
      line-height: 16px;
    }
    .gt-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      margin-left: auto;
      flex: none;
    }
  }

  .gs-empty {
    text-align: center;
    color: #909399;
    font-size: 13px;
    padding: 16px 0;
  }

  /deep/ .el-tree-node__content {
    height: 30px;
    border-radius: 6px;
  }
  /deep/ .el-tree-node.is-current > .el-tree-node__content {
    background: #e5f9fd;
  }
  /deep/ .el-checkbox__input.is-checked .el-checkbox__inner,
  /deep/ .el-checkbox__input.is-indeterminate .el-checkbox__inner {
    background-color: #28b7ee;
    border-color: #28b7ee;
  }
  /deep/ .el-checkbox__input.is-checked + .el-checkbox__label {
    color: #314356;
  }
}
.gs-foot {
  padding: 8px 12px;
  border-top: 1px solid #dcecf4;
  font-size: 12px;
  color: #7f93a6;
  display: flex;
  justify-content: space-between;
  align-items: center;
  .gs-foot-btns {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .gs-clear {
    min-height: 28px;
    padding: 3px 14px;
    font-size: 12px;
    background: #fff;
    border: 1px solid #d7e5ed;
    color: #7f93a6;
    border-radius: 7px;
    cursor: pointer;
  }
  .gs-done {
    min-height: 28px;
    padding: 3px 14px;
    font-size: 12px;
    background: #28b7ee;
    border: none;
    color: #fff;
    border-radius: 7px;
    cursor: pointer;
  }
}
</style>
